import { createContext } from '@lib/mobx/context';
import { ApiError } from '@lib/network/api';
import { CompilationInfo, fetchCompilationInfo } from '@lib/network/rendering';
import {
  getSocialPlatformModule,
  type AnySocialPlatformModule,
  type SocialPostContent,
} from '@lib/social/scheduling/platforms';
import { describeRefusalCode, type SocialPost } from '@lib/social/scheduling/posts';
import { parsePreflightFailure, type PreflightViolation } from '@lib/social/scheduling/preflight';
import { canEditStatus, canRetryStatus } from '@lib/social/scheduling/status';
import { socialCapabilitiesStore } from '@stores/SocialCapabilities';
import { SocialConnection, socialConnectionsStore } from '@stores/SocialConnections';
import { EditPostInput, socialScheduleStore } from '@stores/SocialSchedule';
import { action, computed, makeObservable, observable, reaction, runInAction, when } from 'mobx';

/** A new post for a render, or an existing post the page opened. */
export type PostFormMode = { kind: 'create'; renderId: string } | { kind: 'edit'; postId: string };

/** "Post now" sends the clock at submit time; "at" sends the picked time, null until picked. */
export type PublishWhen = 'now' | 'at';

/** Edit: which parts of the post differ from the stored one. `'now'` means "publish as soon as saved". */
export type PendingEdit = {
  publishAt: number | 'now' | null;
  content: SocialPostContent | null;
};

/** One account the form will post to, with the content it will send. */
export type PostFormTarget = {
  connection: SocialConnection;
  module: AnySocialPlatformModule;
  content: SocialPostContent;
};

/** The 409 codes the API answers when a post's status changed under the user. */
export const isStatusConflict = (error: unknown): boolean =>
  error instanceof ApiError &&
  (error.code === 'social.post.not_editable' ||
    error.code === 'social.post.not_cancellable' ||
    error.code === 'social.post.not_retryable');

/**
 * The compose form's state, one instance per page mount: which accounts,
 * each account's content in its platform's own vocabulary, when to publish,
 * and the last refusal per account. Edit is the same record with one
 * account, holding the stored content. The store owns the submit gate and
 * the request bodies; the components render and forward input.
 */
export class PostFormStore {
  readonly selected = observable.set<string>();
  /**
   * Every account's content, kept past an untick so ticking again restores
   * it. Only the selected ones are sent.
   *
   * FIXME(publishing-copy-content): with several accounts on one platform the
   * caption is typed once per account. Acceptable once: TikTok connects one
   * account per login and no user holds two Facebook Pages yet. When one
   * does, add "use the same text for every account" here, not a shared form.
   */
  readonly contents = observable.map<string, SocialPostContent>();
  readonly violations = observable.map<string, PreflightViolation[]>();
  @observable when: PublishWhen = 'at';
  @observable publishAt: number | null = null;
  @observable submitting = false;
  /** The render the post is about; null until read. */
  @observable.ref render: CompilationInfo | null = null;
  @observable.ref renderError: Error | null = null;
  /** The render's posts, newest first, once read; null before that and after a failed read. */
  @observable.ref renderPostIds: string[] | null = null;

  private disposers: (() => void)[] = [];
  private requestedRenderId: string | null = null;
  private renderRequest = 0;

  readonly mode: PostFormMode;

  /** Two primitives rather than a mode object: the Provider recreates the store when an argument changes by value. */
  constructor(kind: PostFormMode['kind'], id: string) {
    this.mode = kind === 'create' ? { kind, renderId: id } : { kind, postId: id };
    makeObservable(this);
  }

  // ── Lifecycle (from the Provider) ────────────────────────────

  init() {
    socialConnectionsStore.update().catch(() => undefined);
    if (this.mode.kind === 'create') {
      void this.loadRender(this.mode.renderId);
      // One connected account is the obvious target: preselect it once the list is in.
      this.disposers.push(
        when(
          () => socialConnectionsStore.data !== null,
          () => {
            const connections = socialConnectionsStore.data;
            if (connections?.length === 1 && this.canPick(connections[0])) {
              this.toggleAccount(connections[0].id);
            }
          },
        ),
      );
    } else {
      // The post is the page's to load; the form follows the held copy whenever
      // it is fresher (a by-id read of an equally fresh row must not reset typing).
      this.disposers.push(
        reaction(
          () => (this.post ? `${this.post.updatedAt}:${this.post.status}` : null),
          () => this.post && this.adoptPost(this.post),
          { fireImmediately: true },
        ),
      );
    }
  }

  dispose() {
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
  }

  /**
   * Read the render once per id: its info and its posts, two requests with
   * two outcomes (the header words a failed info read and offers Retry; the
   * posts are advisory, and a failed read of them stays silent). A repeat for the same id is a no-op unless forced (the
   * header's Retry). Each read clears the previous outcome and only the
   * latest read may write, so a success after a failure is never shown as
   * the failure and overlapping reads cannot land out of order.
   */
  private async loadRender(renderId: string, force = false) {
    if (!force && this.requestedRenderId === renderId) return;
    this.requestedRenderId = renderId;
    const request = ++this.renderRequest;
    runInAction(() => {
      this.render = null;
      this.renderError = null;
      this.renderPostIds = null;
    });
    void this.loadRenderPosts(renderId, request);
    try {
      const render = await fetchCompilationInfo(renderId);
      if (request !== this.renderRequest) return;
      runInAction(() => (this.render = render));
    } catch (error) {
      if (request !== this.renderRequest) return;
      // 404 (gone) and 403 (another user's) are answers, not failures; the header words them.
      if (!(error instanceof ApiError && (error.status === 404 || error.status === 403))) {
        console.error('Failed to read the render', error);
      }
      runInAction(() => {
        this.renderError = error instanceof Error ? error : new Error(String(error));
      });
    }
  }

  private async loadRenderPosts(renderId: string, request: number) {
    try {
      const ids = await socialScheduleStore.loadRenderPosts(renderId);
      if (request !== this.renderRequest) return;
      runInAction(() => (this.renderPostIds = ids));
    } catch (error) {
      if (request !== this.renderRequest) return;
      // Advisory: the cards say nothing rather than warn under every account, and
      // nothing re-asks until a reload (the header's Retry is the info read's).
      console.error('Failed to read the render’s posts', error);
    }
  }

  /** The header's Retry after a failed read. */
  reloadRender() {
    if (this.requestedRenderId) void this.loadRender(this.requestedRenderId, true);
  }

  /** Edit: the stored post is the form's starting point, adopted again whenever the held copy is replaced. */
  @action
  private adoptPost(post: SocialPost) {
    this.selected.clear();
    this.selected.add(post.connectionId);
    // Null only for a platform this client has no module for (`toSocialPost`
    // throws on a known one without content): the card says unsupported and
    // `targets` leaves the account out.
    if (post.content) this.contents.set(post.connectionId, post.content);
    this.when = 'at';
    this.publishAt = post.publishAt;
    this.violations.clear();
    // An unsupported platform has no module to read the answer, and the card shows no settings line for it.
    if (post.content) {
      void socialCapabilitiesStore.load({ id: post.connectionId, platform: post.platform });
    }
    if (post.renderId) void this.loadRender(post.renderId);
  }

  // ── The post (edit) ──────────────────────────────────────────

  /** The held copy of the post being edited; undefined on create and until the page loads it. */
  @computed
  get post(): SocialPost | undefined {
    return this.mode.kind === 'edit' ? socialScheduleStore.post(this.mode.postId) : undefined;
  }

  /** Edit only: the status no longer lets the user change anything; the fields render read-only. */
  @computed
  get readOnly(): boolean {
    return this.mode.kind === 'edit' && !(this.post && canEditStatus(this.post.status));
  }

  /** Edit only: the status allows a retry. A disconnected account cannot be retried (the API answers 404); cancel is the way out. */
  @computed
  get retryable(): boolean {
    return (
      this.post !== undefined &&
      canRetryStatus(this.post.status) &&
      this.post.attentionReason !== 'connection_missing'
    );
  }

  /** Edit only: the stored post names no render, so there is no media to show and none to read. */
  @computed
  get postWithoutRender(): boolean {
    return this.post !== undefined && this.post.renderId === null;
  }

  /**
   * What the render already did, per account: its posts newest first,
   * without the post being edited (it is not "before" itself). Rows are read
   * from the schedule store by id, so a retry or cancel elsewhere shows here;
   * an id the store no longer holds moved out of a window another view
   * reconciled, and is left out until the next read.
   */
  @computed
  get priorPostsByConnection(): Map<string, SocialPost[]> {
    const byConnection = new Map<string, SocialPost[]>();
    for (const id of this.renderPostIds ?? []) {
      if (this.mode.kind === 'edit' && id === this.mode.postId) continue;
      const post = socialScheduleStore.post(id);
      if (!post) continue;
      const list = byConnection.get(post.connectionId);
      if (list) list.push(post);
      else byConnection.set(post.connectionId, [post]);
    }
    for (const list of byConnection.values()) {
      list.sort((a, b) => b.publishAt - a.publishAt || (a.id < b.id ? -1 : 1));
    }
    return byConnection;
  }

  /** The render's posts to one account, newest first; empty for an account it never went to. */
  priorPosts(connectionId: string): SocialPost[] {
    return this.priorPostsByConnection.get(connectionId) ?? [];
  }

  // ── Accounts ─────────────────────────────────────────────────

  /** Ticking an account for the first time opens its platform's fields at their defaults; unticking keeps what was typed. */
  @action
  toggleAccount(connectionId: string) {
    if (this.selected.has(connectionId)) {
      this.selected.delete(connectionId);
      this.contentChanged(connectionId);
      return;
    }
    const connection = socialConnectionsStore.data?.find((c) => c.id === connectionId);
    const module = connection && getSocialPlatformModule(connection.platform);
    if (!connection || !module) throw new Error(`account ${connectionId} has no platform module`);
    this.selected.add(connectionId);
    if (!this.contents.has(connectionId)) this.contents.set(connectionId, module.defaultContent());
    void socialCapabilitiesStore.load(connection);
  }

  @action
  setContent(connectionId: string, content: SocialPostContent) {
    this.contents.set(connectionId, content);
    this.contentChanged(connectionId);
  }

  /**
   * What an account will send changed, so the refusal recorded against its
   * previous content no longer applies. The one place that rule lives; every
   * mutator above calls it.
   */
  private contentChanged(connectionId: string) {
    this.violations.delete(connectionId);
  }

  @action
  setWhen(when: PublishWhen) {
    this.when = when;
  }

  @action
  setPublishAt(publishAt: number | null) {
    this.publishAt = publishAt;
  }

  /**
   * Whether an account may be picked on create: a platform this client can
   * compose for, and a live connection. One rule for the preselect, the
   * checkbox and the submit gate.
   */
  canPick(connection: SocialConnection): boolean {
    return (
      getSocialPlatformModule(connection.platform) !== undefined &&
      connection.status !== 'needs_reauth'
    );
  }

  /**
   * The selected accounts this client can post to, with what each will send.
   * An unknown platform is left out (its card says so). A known one is
   * selected only through `toggleAccount` or `adoptPost`, both of which give
   * it a content, so a selected account without one is a bug, not a state.
   */
  @computed
  get targets(): PostFormTarget[] {
    const targets: PostFormTarget[] = [];
    for (const connection of socialConnectionsStore.data ?? []) {
      if (!this.selected.has(connection.id)) continue;
      const module = getSocialPlatformModule(connection.platform);
      if (!module) continue;
      const content = this.contents.get(connection.id);
      if (!content) throw new Error(`selected account ${connection.id} has no content`);
      targets.push({ connection, module, content });
    }
    return targets;
  }

  // ── Submit gate ──────────────────────────────────────────────

  /** Why the form cannot be submitted yet, in the order the form shows them; empty when it can. */
  @computed
  get blockers(): string[] {
    const blockers = new Set<string>();
    if (this.mode.kind === 'create' && this.targets.length === 0) {
      blockers.add('Choose at least one account.');
    }
    if (this.when === 'at' && this.publishAt === null) blockers.add('Select when to publish.');
    for (const { connection, module, content } of this.targets) {
      const name = connection.displayName ?? module.label;
      // Edit keeps going (reconnect, then retry is the documented repair); create cannot target the account.
      if (this.mode.kind === 'create' && !this.canPick(connection)) {
        blockers.add(`${name} needs to be reconnected first.`);
        continue;
      }
      const capabilities = socialCapabilitiesStore.capabilities(connection.id);
      // The platform's verdict on the account is the whole answer: no field would get the post through.
      if (capabilities && !capabilities.available) {
        blockers.add(
          `${name}: ${describeRefusalCode(capabilities.code) ?? 'cannot post right now.'}`,
        );
        continue;
      }
      const missing = module.missingField(content, capabilities);
      if (missing) blockers.add(`${name}: ${missing}`);
    }
    return [...blockers];
  }

  @computed
  get canSubmit(): boolean {
    if (this.submitting || this.readOnly || this.blockers.length > 0) return false;
    return this.mode.kind === 'create' || this.pendingEdit !== null;
  }

  /**
   * Edit only: what differs from the stored post, or null when nothing does.
   * "now" is an intent, resolved to the clock at submit, so this computed
   * stays pure and the time sent is the time the button was pressed.
   */
  @computed
  get pendingEdit(): PendingEdit | null {
    const post = this.post;
    if (!post) return null;
    const publishAt =
      this.when === 'now'
        ? 'now'
        : this.publishAt !== null && this.publishAt !== post.publishAt
          ? this.publishAt
          : null;
    const edited = this.contents.get(post.connectionId);
    const content =
      edited && JSON.stringify(edited) !== JSON.stringify(post.content) ? edited : null;
    return publishAt === null && content === null ? null : { publishAt, content };
  }

  /**
   * Schedule (create) or save (edit). Returns the posts the API answered
   * with. A preflight refusal lands in `violations` per account and is
   * rethrown; every other error is rethrown as is, for the page to word.
   */
  async submit(): Promise<SocialPost[]> {
    if (!this.canSubmit) throw new Error('submit called while the form cannot be submitted');
    // A refusal belongs to the submit that produced it; this one starts clean.
    runInAction(() => {
      this.submitting = true;
      this.violations.clear();
    });
    try {
      const publishAt = this.when === 'now' ? Date.now() : this.publishAt;
      if (this.mode.kind === 'create') {
        if (publishAt === null) throw new Error('create without a publish time');
        return await socialScheduleStore.create({
          renderId: this.mode.renderId,
          publishAt,
          targets: this.targets.map(({ connection, content }) => ({
            connectionId: connection.id,
            content,
          })),
        });
      }
      const pending = this.pendingEdit;
      if (!pending) throw new Error('edit without changes');
      const input: EditPostInput = {};
      if (pending.publishAt === 'now') input.publishAt = Date.now();
      else if (pending.publishAt !== null) input.publishAt = pending.publishAt;
      if (pending.content) input.content = pending.content;
      return [await socialScheduleStore.edit(this.mode.postId, input)];
    } catch (error) {
      const refused = parsePreflightFailure(error);
      if (refused) {
        runInAction(() => {
          for (const [connectionId, violations] of refused)
            this.violations.set(connectionId, violations);
        });
      }
      throw error;
    } finally {
      runInAction(() => (this.submitting = false));
    }
  }
}

export const { Provider: PostFormProvider, useStore: usePostForm } = createContext(PostFormStore, {
  init: (store) => store.init(),
  dispose: (store) => store.dispose(),
});
